import React from 'react';
import { Users as _Users, Calendar, Clock, Users } from 'lucide-react';

/**
 * UrgencyIndicators Component
 * Social proof indicators to create urgency (viewing count, bookings, availability)
 */
export default function UrgencyIndicators({
  indicators = [],
  variant = 'compact' // 'compact' or 'expanded'
}) {
  if (!indicators || indicators.length === 0) {
    return null;
  }

  const getIcon = (type) => {
    switch (type) {
      case 'viewing':
        return <Users className="w-4 h-4 text-rose-600" />;
      case 'bookings':
        return <Calendar className="w-4 h-4 text-blue-600" />;
      case 'recent':
        return <Clock className="w-4 h-4 text-amber-600" />;
      case 'availability':
        return <Calendar className="w-5 h-5 text-rose-600" />;
      default:
        return null;
    }
  };

  const getStyles = (type) => {
    switch (type) {
      case 'viewing':
        return {
          bg: 'bg-rose-50',
          border: 'border-rose-200',
          text: 'text-rose-700',
          borderLeft: 'border-rose-400'
        };
      case 'bookings':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          borderLeft: 'border-blue-400'
        };
      case 'recent':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-700',
          borderLeft: 'border-amber-400'
        };
      case 'availability':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          borderLeft: 'border-red-400'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-700',
          borderLeft: 'border-gray-400'
        };
    }
  };

  if (variant === 'compact') {
    return (
      <div className="mb-6 flex flex-wrap gap-3">
        {indicators.map((indicator, index) => {
          const styles = getStyles(indicator.type);
          return (
            <div
              key={index}
              className={`flex items-center gap-2 px-4 py-2 ${styles.bg} border ${styles.border} rounded-lg`}
            >
              {getIcon(indicator.type)}
              <span className={`text-sm ${styles.text}`}>
                {indicator.highlight ? (
                  <>
                    <strong>{indicator.highlight}</strong> {indicator.text}
                  </>
                ) : (
                  indicator.text
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Expanded variant for profile pages
  return (
    <div className="space-y-3 mb-6">
      {indicators.map((indicator, index) => {
        const styles = getStyles(indicator.type);
        return (
          <div
            key={index}
            className={`${styles.bg} border-l-4 ${styles.borderLeft} p-4 rounded-r-lg`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">{getIcon(indicator.type)}</div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {indicator.highlight ? (
                    <>
                      <strong>{indicator.highlight}</strong> {indicator.text}
                    </>
                  ) : (
                    indicator.text
                  )}
                </p>
                {indicator.subtext && (
                  <p className="text-xs text-gray-600 mt-1">{indicator.subtext}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
